"use client";
import React from "react";
import useSWR from "swr";
import { Surcharge } from "@/components/surcharge/surcharge";
import { giftCardItem } from "@/utils/giftCardUtils";
import { Category } from "@/dto/category";
import { Menu } from "@/dto/menu";
import { GetMerchantPaymentConfigResponse } from "@/dataLayer/getMerchantPaymentConfig";
import { Item, Layout } from "@/dto/item";
import { useTranslations } from "next-intl";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { ROUTE_SHOP, ROUTE_GIFTCARD } from "@/utils/constants";
import { MenuCategoriesList } from "@/components/menu/menuCategoriesList";
import { getShopMenuDataAction } from "../actions";

interface ShopMenuItemsProps {
  merchantId: string;
  merchantName: string;
  searchParams?: { [key: string]: string | string[] | undefined };
  menu: Menu | null;
  retailCategories: Category[];
  itemTemplate?: string;
  merchantPaymentConfig: GetMerchantPaymentConfigResponse;
  isSimpleStorefront: boolean;
  layout?: Layout;
}

export const ShopMenuItems = ({
  merchantId,
  merchantName,
  searchParams,
  menu,
  retailCategories,
  itemTemplate,
  merchantPaymentConfig,
  isSimpleStorefront,
  layout = "default",
}: ShopMenuItemsProps) => {
  const t = useTranslations("category");
  const query = (searchParams?.query as string) ?? "";

  const { data } = useSWR(
    ["shopMenuData", merchantId],
    () => getShopMenuDataAction(merchantId),
    {
      ...(menu && { fallbackData: { menu, retailItems: retailCategories } }),
      revalidateOnMount: false,
      revalidateOnFocus: true,
    }
  );

  const activeMenu = data?.menu ?? menu;
  const activeRetailCategories = data?.retailItems ?? retailCategories;

  const getItemHref = (item: Item) =>
    item.id === giftCardItem.id
      ? ROUTE_GIFTCARD
      : `${ROUTE_SHOP}/${getItemUrl(item)}`;

  if (!activeMenu) return null;

  return (
    <div className="scroll-smooth">
      <div className="sm:hidden text-center">
        {!isSimpleStorefront &&
          Boolean(merchantPaymentConfig?.surcharging?.rate) && (
            <Surcharge
              surchargeRate={merchantPaymentConfig.surcharging?.rate}
            />
          )}
      </div>
      <MenuCategoriesList
        menu={activeMenu}
        merchantName={merchantName}
        itemTemplate={itemTemplate}
        query={query}
        retailCategories={activeRetailCategories}
        getItemHref={getItemHref}
        uncategorizedLabel={t("uncategorized")}
        uncategorizedItemFilter={(item: any) => item.itemType === "REGULAR"}
        layout={layout}
      />
    </div>
  );
};
